import React, { useEffect } from 'react';
import styled from 'styled-components/macro';
import CustomTextField from 'components/Input/CustomTextField';
import {
    SET_CONSOLIDATION,
    GET_CONSOLIDATION_DETAIL,
} from 'store/key';
import { useDispatch, useSelector } from 'react-redux';
import CustomOutlineSelect from 'components/Input/CustomOutlineSelect';
import CustomDatePicker from 'components/Input/CustomDatePicker';
import UploadWrapper from 'components/UploadWrapper';
import CustomTable from 'components/CustomTable';
import TextHeader from 'components/CustomText/TextHeader';
import TextSubHeader from 'components/CustomText/TextSubHeader';
import useAttendanceMember from './useAttendanceMember';
import useConsolidationData from './useConsolidationData';
import LoadingIndicator from 'components/LoadingIndicator';
import { typeOfReview } from 'constants/globalEnum';
import API from 'api/call';
import { INIT_APPLICATION, AMENDMENT_FORM } from 'utility/postApprovalLookUp';

const SRow = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    & > div:nth-child(1) {
        width: 200px;
        flex-shrink: 0;
    }

    & > div:nth-child(2) {
        width: 400px;
    }
`;

const SLayout = styled.div`
    padding: 15px;
`;

const SDecisionLayout = styled.div`
    width: 500px;
`;

const SSelectLayout = styled.div`
    margin-top: 10px;
    /* width: 500px; */
`;

const SCommentLayout = styled.div`
    margin-top: 30px;
    /* width: 500px; */
`;

const SDocumentUploadLayout = styled.div`
    margin-top: 20px;
    /* display: flex;
    align-items: flex-start; */
`;

const Consolidation = ({
    formId,
    isPermission,/** Screen of View only when check consolidation APPROVED, ACKNOWLEDGED, DISAPPROVED */
    documentType,
}) => {
    const dispatch = useDispatch();
    const consolidationDetail = useSelector((state) => state.consolidationDetail);
    const {
        decision,
        comment,
        decisionDate,
        approvalExpiryDate,
        refNo,
        uploadTempId,
    } = consolidationDetail.detail;

    const {
        forceErrorDisplay,
        remoteDetail,
        rows,
        uploadTempName,
        loading,
        tempFile,
    } = consolidationDetail;
    
    /** for prototype purpose only */
    const isViewOnly = !isPermission;
    const isPostApproval = documentType !== INIT_APPLICATION;
    const showApprovalLetter = (isViewOnly && uploadTempId && uploadTempName) || !isViewOnly;

    const {
        getDecisionMapingList,
    } = useConsolidationData({});

    const reviewType = remoteDetail?.meetingAttendance?.typeOfReview;
    const decisionResultList = getDecisionMapingList({ documentType, reviewType, isPostApproval, isViewOnly });

    const { columns } = useAttendanceMember({ isViewOnly, rows });

    useEffect(() => {
        dispatch({
            type: SET_CONSOLIDATION,
            payload: {
                forceErrorDisplay: false,
            },
        });
        dispatch({
            type: GET_CONSOLIDATION_DETAIL,
            payload: {
                formId,
                documentType,
            },
        });
        // eslint-disable-next-line
    }, []);

    const setData = (key, value, isDetail) => {
        if (isDetail) {
            dispatch({
                type: SET_CONSOLIDATION,
                payload: {
                    detail: {
                        ...consolidationDetail.detail,
                        [key]: value,
                    }
                },
            });
        } else {
            dispatch({
                type: SET_CONSOLIDATION,
                payload: {
                    [key]: value
                },
            });
        }
    };

    const changeSelected = (e) => {
        const { value } = e.target;
        setData('decision', Number(value), true);
    };

    const onFileUploaded = (list) => {
        dispatch({
            type: SET_CONSOLIDATION,
            payload: {
                detail: {
                    ...consolidationDetail.detail,
                    uploadTempId: list[0].key,
                },
                uploadTempName: list[0].fileName,
                tempFile: list[0].file,
            },
        });
    };

    return (
        <SLayout>
            {loading ? (
                <div style={{ height: '100px', textAlign: 'center' }}>
                    <LoadingIndicator /> <div style={{ marginLeft: 10 }}>Loading Consolidation...</div>
                </div>
            ) : (remoteDetail && <>
                <TextHeader>Please provide your decision</TextHeader>
                <SDecisionLayout>
                    <SSelectLayout>
                        <CustomOutlineSelect
                            value={decision}
                            onChange={changeSelected}
                            choices={decisionResultList}
                            placeholder={'Please choose your decision'}
                            isRequired
                            forceErrorDisplay={forceErrorDisplay}
                            isValidValue={(isValid) => setData('decisionValid', isValid)}
                            disabled={isViewOnly}
                            validators={['isRequired']}
                        />
                    </SSelectLayout>

                    <SSelectLayout style={{ marginTop: 30 }}>
                        <CustomDatePicker
                            name={'decisionDate'}
                            label={'Decision Date'}
                            value={decisionDate}
                            maxDate={Date.now()}
                            onChange={(date) => {
                                setData('decisionDate', date, true);
                            }}
                            disabled={isViewOnly}
                            validators={['isRequired']}
                            forceErrorDisplay={forceErrorDisplay}
                        />
                    </SSelectLayout>

                    {!isPostApproval && (
                        <SSelectLayout style={{ marginTop: 30 }}>
                            <CustomDatePicker
                                name={'approvalExpiryDate'}
                                label={'Approval Expiry date'}
                                value={approvalExpiryDate}
                                onChange={(date) => {
                                    setData('approvalExpiryDate', date, true);
                                }}
                                disabled={isViewOnly}
                            />
                        </SSelectLayout>
                    )}

                    {!isPostApproval && (
                        <CustomTextField
                            style={{ marginTop: 30 }}
                            label={'IRB/REC Reference No.'}
                            value={refNo}
                            onChange={(e) => setData('refNo', e.target.value, true)}
                            forceErrorDisplay={forceErrorDisplay}
                            validators={['isRequired']}
                            isValidValue={(isValid) => setData('refNoValid', isValid)}
                            disabled={isViewOnly}
                            maxLength={255}
                        />
                    )}

                    <SCommentLayout>
                        <CustomTextField
                            isTextArea
                            placeholder={'Leave your comment here'}
                            value={comment}
                            textAreaMinRow={6}
                            onChange={(e) => setData('comment', e.target.value, true)}
                            maxLength={1000}
                            isValidValue={(valid) => setData('commentValid', valid)}
                            forceErrorDisplay={true}
                            disabled={isViewOnly}
                        />
                    </SCommentLayout>

                    <SDocumentUploadLayout>
                        Upload signed approval letter to Confirm: {!showApprovalLetter ? '-' : ''}
                        {showApprovalLetter && <div style={{ marginTop: 10 }}>
                            <UploadWrapper
                                onFileUploadFinish={onFileUploaded}
                                multiple={false}
                                isRequired
                                files={
                                    uploadTempId
                                        ? [{ name: uploadTempName, downloadId: uploadTempId, file: tempFile }]
                                        : []
                                }
                                inputStyle
                                inputStyleText={'Upload Approval Letter'}
                                enableRemove={!isViewOnly}
                                onRemove={() => {
                                    setData('uploadTempId', null, true);
                                }}
                                enableDownload={true}
                                downloadType="temp"
                                apiUrl={API.consolidation.downloadApprovalLetter}
                                newRequestData={{ docType: documentType, docId: formId, }}
                                isLocalDownload={remoteDetail.uploadTempId !== uploadTempId}
                            />
                        </div>}
                            {forceErrorDisplay && (!uploadTempName || !uploadTempId) && [INIT_APPLICATION, AMENDMENT_FORM].includes(documentType) && (
                            <div style={{ marginTop: 10, color: 'red' }}>Approval letter should be uploaded</div>
                        )}
                    </SDocumentUploadLayout>
                </SDecisionLayout>
                <SCommentLayout>
                    <TextHeader>Update Meeting Attendance</TextHeader>

                    <div style={{ marginTop: 20 }}>
                        <SRow>
                            <div>Type of Review: </div>
                            <div>
                                {typeOfReview.filter(item => item.key === reviewType)[0]?.display ?? '-'}
                            </div>
                        </SRow>

                        <SRow>
                            <div>Review Due Date: </div>
                            <div>
                                {remoteDetail?.meetingAttendance?.reviewDueDate ?? '-'}
                            </div>
                        </SRow>
                        <SRow>
                            <div>Review Meeting Date: </div>
                            <div>
                                {remoteDetail?.meetingAttendance?.reviewMeetingDate ?? '-'}
                            </div>
                        </SRow>
                        <SRow>
                            <div>Review Circulation Date: </div>
                            <div>
                                {remoteDetail?.meetingAttendance?.reviewCirculationDate ?? '-'}
                            </div>
                        </SRow>
                    </div>

                    <div style={{ display: 'flex', marginTop: 20, alignItems: 'center' }}>
                        <TextSubHeader>Panel Member Table:</TextSubHeader>
                    </div>

                    <div style={{ marginTop: 20 }}>
                        <CustomTable rows={rows} columns={columns} />
                    </div>
                </SCommentLayout>
            </>)}
        </SLayout>
    );
};

export default Consolidation;
